import {ActiveFilters} from './ActiveFilters';
import {CatalogControls} from './CatalogControls';

import {useCatalog, useCatalogDispatch} from "../../store/catalog";
import {useEffect} from '@wordpress/element';
import {ProductCard, ProductCardSkeleton} from "../common/ProductCard";
import {SubcategoryCarousel} from "./SubcategoryCarousel";
import {WordpressAlignWide} from "../common/WordpressAlignWide";
import {Pagination} from "./Pagination";
import {Breadcrumbs} from "./Breadcrumbs";
import {useCategories} from "../../store/categories";

const EmptyState = () => {
    const {resetFilters} = useCatalog();
    return (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-base-300" fill="none"
                 viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <div>
                <p className="text-lg font-black pt-dark-blue">No products found</p>
                <p className="text-sm text-base-content/50 mt-1">Try adjusting your filters or search term</p>
            </div>
            <button
                onClick={resetFilters}
                className="btn btn-sm btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary rounded-full mt-2"
            >
                Clear all filters
            </button>
        </div>
    );
};

export const Catalog = () => {
    const {data, loading, error, filterValues} = useCatalog();
    const {loading: categoriesLoading, tree} = useCategories();
    const selectedCategory = filterValues.categories;
    const dispatch = useCatalogDispatch();

    useEffect(() => {
        dispatch.init();

        const onPop = () => dispatch.init();
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [dispatch]);

    const isEmpty = !loading && data && data.products?.length === 0;

    return (
        <WordpressAlignWide>
            <Breadcrumbs/>
            {error && (<div className="alert alert-error mb-6 shadow-sm">Error loading products.</div>)}

            <div className="flex flex-col gap-4">
                <main className="w-full">
                    <div className="mb-8">
                        <h1 className="text-4xl font-black pt-dark-blue mb-8">
                            {categoriesLoading ? (
                                <div className="h-10 bg-base-300 rounded w-48 animate-pulse"/>
                            ) : (
                                selectedCategory?.name || 'Marketplace'
                            )}
                        </h1>

                        <SubcategoryCarousel
                            subcategories={selectedCategory ? (selectedCategory.subcategories || []) : tree}
                            loading={categoriesLoading}
                        />

                        <div className="flex flex-col gap-4 mb-10">
                            <CatalogControls/>
                            <ActiveFilters/>
                        </div>
                    </div>

                    {isEmpty ? (
                        <EmptyState/>
                    ) : (
                        <>
                            <div
                                className="mb-4 grid gap-8 justify-items-center [grid-template-columns:repeat(auto-fit,minmax(18rem,1fr))]">
                                {(loading || !data) ? (
                                    Array.from({length: 9}).map((_, i) => (
                                        <ProductCardSkeleton key={i}/>
                                    ))
                                ) : (
                                    data.products.map(p => (
                                        <ProductCard product={p} key={p.sku}/>
                                    ))
                                )}
                            </div>
                            <Pagination variant="footer"/>
                        </>
                    )}
                </main>
            </div>
        </WordpressAlignWide>
    );
};